import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  HeartIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../config/api';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    businessType: '',
    message: '',
    agreeToPrivacy: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`/v1/contacts/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          email: formData.email,
          business_type: formData.businessType,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('문의 접수에 실패했습니다.');
      }

      const result = await response.json();

      // 성공 처리
      alert(
        result.message ||
          '문의가 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.'
      );

      // 폼 초기화
      setFormData({
        name: '',
        company: '',
        phone: '',
        email: '',
        businessType: '',
        message: '',
        agreeToPrivacy: false,
      });
    } catch (error: any) {
      console.error('문의 접수 오류:', error);
      setError(
        error.message || '문의 접수 중 오류가 발생했습니다. 다시 시도해주세요.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const businessTypes = [
    { value: 'studyroom', label: '독서실', icon: BuildingOfficeIcon },
    { value: 'academy', label: '학원', icon: AcademicCapIcon },
    { value: 'gym', label: '헬스장/피트니스', icon: HeartIcon },
    { value: 'smallbusiness', label: '소기업', icon: UserGroupIcon },
    { value: 'other', label: '기타', icon: UserGroupIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-sm">
                <SparklesIcon className="h-5 w-5 text-white" />
              </div>
              <h1 className="ml-3 text-xl font-semibold text-gray-900">
                어텐드
              </h1>
            </Link>
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              메인으로 돌아가기
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* 왼쪽: 문의 정보 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                어텐드와 함께 시작하세요
              </h2>

              <div className="space-y-6">
                <div className="flex items-start">
                  <PhoneIcon className="h-6 w-6 text-blue-600 mt-1" />
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      전화 상담
                    </h3>
                    <p className="text-gray-600">010-9042-1900</p>
                    <p className="text-sm text-gray-500">평일 9:00-18:00</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <EnvelopeIcon className="h-6 w-6 text-green-600 mt-1" />
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900 mb-1">이메일</h3>
                    <p className="text-gray-600">admin@newattend.com</p>
                    <p className="text-sm text-gray-500">
                      24시간 접수, 24시간 내 답변
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <ClockIcon className="h-6 w-6 text-orange-600 mt-1" />
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      상담 시간
                    </h3>
                    <p className="text-gray-600">평일 9:00 - 18:00</p>
                    <p className="text-sm text-gray-500">토/일/공휴일 휴무</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 오른쪽: 문의 폼 */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                사업 문의 및 상담 신청
              </h2>
              <p className="text-gray-600 mb-8">
                전문 상담사가 귀하의 비즈니스에 맞는 최적의 솔루션을
                제안해드립니다.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      담당자 성함 *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="홍길동"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      시설/업체명 *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="OO독서실"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      연락처 *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="010-1234-5678"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      이메일 *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="contact@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="businessType"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    사업 분야 *
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    required
                    value={formData.businessType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="">사업 분야를 선택해주세요</option>
                    {businessTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    문의 내용
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="궁금한 사항이나 요청사항을 자세히 적어주세요.&#10;&#10;예시:&#10;- 현재 운영 중인 시설 규모&#10;- 관심 있는 기능&#10;- 예상 도입 시기&#10;- 예산 범위&#10;등"
                  />
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="agreeToPrivacy"
                      name="agreeToPrivacy"
                      type="checkbox"
                      required
                      checked={formData.agreeToPrivacy}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="agreeToPrivacy" className="text-gray-600">
                      개인정보 수집 및 이용에 동의합니다. 수집된 정보는 상담
                      목적으로만 사용되며, 상담 완료 후 6개월 후 자동
                      삭제됩니다. *
                    </label>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-4 px-6 rounded-sm font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? '접수 중...' : '문의 접수하기'}
                  </button>
                </div>

                <div className="text-center text-sm text-gray-500">
                  문의 접수 후 <strong>24시간 내</strong>에 담당자가
                  연락드립니다.
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* 하단: 자주 묻는 질문 */}
        <div className="mt-16">
          <div className="bg-white rounded-lg p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              자주 묻는 질문
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  💰 도입 비용은 얼마나 드나요?
                </h4>
                <p className="text-gray-600 text-sm">
                  시설 규모와 필요한 기능에 따라 달라집니다. 무료 상담을 통해
                  맞춤형 견적을 제공해드립니다.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  ⏱️ 설치 기간은 얼마나 걸리나요?
                </h4>
                <p className="text-gray-600 text-sm">
                  일반적으로 1-2주 정도 소요되며, 시설 규모에 따라 조정될 수
                  있습니다.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  🔧 기술 지원은 어떻게 받나요?
                </h4>
                <p className="text-gray-600 text-sm">
                  평일 9-18시 전화/이메일 지원과 원격 접속을 통한 즉시 해결을
                  제공합니다.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  📱 모바일에서도 사용 가능한가요?
                </h4>
                <p className="text-gray-600 text-sm">
                  네, 웹/모바일 앱 모두 지원하며 실시간 동기화로 어디서든 관리
                  가능합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
